/**
 * Ayudas de formato compartidas por las pantallas de **Gestión**.
 *
 * Las fechas llegan como `Date` de MySQL serializado en ISO, así que se muestran
 * en formato peruano (`d/m/Y`), y los importes con el `formatCurrency` del panel.
 */

/**
 * Fecha corta (`d/m/Y`), o un guion.
 *
 * Las fechas con hora llegan como `Date` de MySQL serializado en ISO y las que
 * no tienen hora como `AAAA-MM-DD`; estas últimas se formatean a mano porque
 * `new Date('2026-09-01')` las interpreta en UTC y en Perú se verían un día
 * antes.
 */
export function fechaCorta(valor: string | null | undefined): string {
  if (!valor) return '—';

  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);

  if (partes) return `${partes[3]}/${partes[2]}/${partes[1]}`;

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) return valor;

  return fecha.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Fecha con hora (`d/m/Y H:i`), o un guion. */
export function fechaHora(valor: string | null | undefined): string {
  if (!valor) return '—';

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) return valor;

  return fecha.toLocaleString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Importe sin símbolo: el `S/` va en el encabezado de la columna. */
export function monto(valor: number | null | undefined): string {
  const cifra = Number(valor ?? 0);

  if (!Number.isFinite(cifra)) return '0.00';

  return cifra.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Fecha de hoy en el formato del campo `date` (`AAAA-MM-DD`). */
export function hoyIso(): string {
  const fecha = new Date();
  const desplazamiento = fecha.getTimezoneOffset() * 60_000;

  return new Date(fecha.getTime() - desplazamiento).toISOString().slice(0, 10);
}

/** Fecha de hace `dias` días atrás (`AAAA-MM-DD`), en la zona local. */
export function hoyMenosDias(dias: number): string {
  const fecha = new Date();
  fecha.setDate(fecha.getDate() - Number(dias || 0));

  const desplazamiento = fecha.getTimezoneOffset() * 60_000;

  return new Date(fecha.getTime() - desplazamiento).toISOString().slice(0, 10);
}

/** Primer día del mes actual (`AAAA-MM-DD`). */
export function inicioDeMesIso(): string {
  return `${hoyIso().slice(0, 7)}-01`;
}

/** Distintivo del estado de un comprobante (`1` activo, `2` anulado). */
export function tonoEstado(estado: number): 'success' | 'danger' | 'neutral' {
  if (estado === 1) return 'success';
  if (estado === 2) return 'danger';

  return 'neutral';
}

/** Etiqueta del estado de un comprobante. */
export function etiquetaEstado(estado: number): string {
  if (estado === 1) return 'ACTIVO';
  if (estado === 2) return 'ANULADO';

  return '—';
}
